// Send Logs' bundle (docs/features/send-logs.md › The bundle): the text made on this device — the header, the console
// ring (buffer.js) and the log panel's lines (core/log.js) — redacted as a whole, last; then raw DEFLATE, its SHA-256,
// and the `logsPart` pieces the wire carries (32 KiB of compressed bytes each, base64; the whole's size and hash in
// every piece). Pure: no DOM, no link — send.js and the tests call these.
import { redact } from './redact.js'

export const PIECE = 32768
const enc = new TextEncoder()

/// A bundle's id: "lg-" and twelve random hex digits ("lg-0b6c1d2e-3f40").
export function bundleID() {
  const b = new Uint8Array(6)
  globalThis.crypto.getRandomValues(b)
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('')
  return `lg-${hex.slice(0, 8)}-${hex.slice(8)}`
}

/// The bundle's text. `device`: { kind, app, model, os }; `lines` the ring's, `appLog` the panel's; `made` a Date.
export function makeBundle({ lines = [], appLog = [], device = {}, asked = false, made = new Date() }) {
  const text = [
    '# Cody logs',
    `# device: ${device.kind || 'browser'}`,
    `# app: ${device.app || '?'}`,
    `# model: ${device.model || '?'}`,
    `# os: ${device.os || '?'}`,
    `# made: ${new Date(made).toISOString()}`,
    `# asked: ${asked ? 'yes' : 'no'}`,
    '== log ==',
    ...lines,
    '== app log ==',
    ...appLog,
  ].join('\n') + '\n'
  return redact(text)
}

/// This browser, as the header says it: the app's build, the device and its OS (Chromium's client hints when it has
/// them — a phone's model only comes that way — else the user agent's words).
export async function describeDevice({ nav = globalThis.navigator, stamp = {} } = {}) {
  const ua = String(nav?.userAgent || '')
  const app = `web ${stamp.commit || 'unstamped'} (${stamp.stamp ? stamp.stamp.replace(/^build /, '') : stamp.builtAt ? new Date(stamp.builtAt).toISOString() : 'local'})`
  let hints = null
  try { hints = await nav?.userAgentData?.getHighEntropyValues?.(['model', 'platform', 'platformVersion', 'fullVersionList']) } catch {}
  const browser = browserOf(ua, hints)
  const model = [hints?.model, browser].filter(Boolean).join(' · ') || ua.slice(0, 120) || '?'
  const os = hints?.platform ? `${hints.platform} ${hints.platformVersion || ''}`.trim() : osOf(ua)
  return { kind: 'browser', app, model, os }
}

function browserOf(ua, hints) {
  const brand = (hints?.fullVersionList || []).find((b) => !/Not.A.Brand|Chromium/i.test(b.brand))
  if (brand) return `${brand.brand} ${brand.version}`
  for (const [name, re] of [['Edge', /Edg\/([\d.]+)/], ['Chrome', /(?:Chrome|CriOS)\/([\d.]+)/], ['Firefox', /(?:Firefox|FxiOS)\/([\d.]+)/], ['Safari', /Version\/([\d.]+).*Safari/]]) {
    const m = ua.match(re)
    if (m) return `${name} ${m[1]}`
  }
  return ''
}

function osOf(ua) {
  let m
  if ((m = ua.match(/Android ([\d.]+)/))) return 'Android ' + m[1]
  if ((m = ua.match(/(?:iPhone|CPU) OS ([\d_]+)/))) return 'iOS ' + m[1].replace(/_/g, '.')
  if ((m = ua.match(/Mac OS X ([\d_.]+)/))) return 'macOS ' + m[1].replace(/_/g, '.')
  if ((m = ua.match(/Windows NT ([\d.]+)/))) return 'Windows NT ' + m[1]
  if (/Linux/.test(ua)) return 'Linux'
  return '?'
}

/// The text as raw DEFLATE (no zlib header), as the Mac inflates it.
export async function compress(text) {
  const stream = new Blob([enc.encode(text)]).stream().pipeThrough(new CompressionStream('deflate-raw'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

export async function sha256Hex(bytes) {
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', bytes))
  return [...digest].map((x) => x.toString(16).padStart(2, '0')).join('')
}

export function toBase64(bytes) {
  let s = ''
  for (let i = 0; i < bytes.length; i += 8192) s += String.fromCharCode(...bytes.subarray(i, i + 8192))
  return btoa(s)
}

export function fromBase64(text) {
  const s = atob(text)
  const out = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i)
  return out
}

/// The `logsPart` messages for the compressed bytes, in order (one piece even for an empty bundle).
export function pieces(id, bytes, sha256) {
  const parts = Math.max(1, Math.ceil(bytes.length / PIECE))
  const out = []
  for (let part = 0; part < parts; part++) {
    out.push({ t: 'logsPart', id, part, parts, bytes: bytes.length, sha256, data: toBase64(bytes.subarray(part * PIECE, (part + 1) * PIECE)) })
  }
  return out
}
